// Template instantiation service — gated verbatim instantiate into an
// ordinary project (Plan B1 Task 3).
//
// Fail-closed: every source file the registry entry declares is fetched and
// sha256-verified against its manifest BEFORE the deterministic compiler
// (`@cogo/template`'s `instantiate`) ever runs, and the two fidelity gates
// (`diffDeclaredOnly`/`verifyStructureHash`) run on the compiled output
// before a project is ever created — a failure at any stage returns a typed
// failure list and never leaves a partial project behind.

import type { AppBundle, Json } from '@cogo/template'
import { diffDeclaredOnly, instantiate, verifyStructureHash } from '@cogo/template'
import * as api from '@/services/project-api'
import { buildLoadedTemplateFromFiles } from '@/features/page-editor/services/template-loader'
import type { TemplateRegistryEntry } from './registry'

/**
 * CAVEAT: this is not an exhaustive result union. `api.createProject` (and
 * the network fetches inside `fetchAndVerifySourceBundle`) can still THROW
 * — a rejection bubbles out of `instantiateTemplateToProject` as a promise
 * rejection instead of an `{ ok: false, ... }` value. Every caller MUST
 * wrap its call in try/catch and treat a caught rejection the same as an
 * `ok: false` result (see the template-proposal card's onAccept handler in
 * shell/ide-v2/components/chat-messages.tsx, Plan B1 Task 4).
 */
export type InstantiateToProjectResult =
  | { ok: true; projectId: string }
  | { ok: false; failures: { code: string; message: string }[] }

function toHex(bytes: Uint8Array): string {
  let out = ''
  for (const b of bytes) out += b.toString(16).padStart(2, '0')
  return out
}

async function sha256OfBytes(bytes: ArrayBuffer): Promise<string> {
  const digest = await globalThis.crypto.subtle.digest('SHA-256', bytes)
  return `sha256:${toHex(new Uint8Array(digest))}`
}

/**
 * Fetches every `entry.files[i].path` from `entry.basePath`, sha256-verifies
 * the raw bytes against the manifest, and parses the verified bytes as JSON.
 * `template-structure-hash-mismatch` is a deliberate, documented reuse of
 * the closed failure-code set (§7 of the `@cogo/template` contract) — this
 * function has no fetch/network code of its own to add a new code for
 * "served source drifted from the registered manifest," and that's exactly
 * what a hash mismatch here means (the same failure class the structureHash
 * gate reports, just caught one step earlier, before the source bundle is
 * even assembled).
 */
async function fetchAndVerifySourceBundle(
  entry: TemplateRegistryEntry,
): Promise<{ ok: true; bundle: AppBundle } | { ok: false; failures: { code: string; message: string }[] }> {
  const failures: { code: string; message: string }[] = []
  const files: Record<string, Json> = {}

  await Promise.all(
    entry.files.map(async ({ path, sha256 }) => {
      let bytes: ArrayBuffer
      try {
        const res = await fetch(`${entry.basePath}/${path}`)
        if (!res.ok) {
          failures.push({
            code: 'template-structure-hash-mismatch',
            message: `source file drift: ${path} (fetch failed with status ${res.status})`,
          })
          return
        }
        bytes = await res.arrayBuffer()
      } catch (err) {
        failures.push({
          code: 'template-structure-hash-mismatch',
          message: `source file drift: ${path} (fetch threw: ${err instanceof Error ? err.message : String(err)})`,
        })
        return
      }

      const actual = await sha256OfBytes(bytes)
      if (actual !== sha256) {
        failures.push({ code: 'template-structure-hash-mismatch', message: `source file drift: ${path}` })
        return
      }

      try {
        files[path] = JSON.parse(new TextDecoder().decode(bytes)) as Json
      } catch (err) {
        failures.push({
          code: 'template-structure-hash-mismatch',
          message: `source file drift: ${path} (invalid JSON: ${err instanceof Error ? err.message : String(err)})`,
        })
      }
    }),
  )

  if (failures.length > 0) return { ok: false, failures }
  return { ok: true, bundle: { files } }
}

/**
 * Compiles `entry`'s registered template package against its own served
 * source (re-fetched + hash-verified, never trusted from cache) into a
 * plain, ordinary-project `LoadedApp`, creates the project, and — as a
 * best-effort, non-fatal side effect — records the instantiation lineage
 * (`templateId`/`version`/`structureHash`) on the created project so it can
 * later be traced back to the template it was compiled from.
 *
 * Flow (all steps fail-closed; no partial project is ever created):
 *  1. fetch + sha256-verify every declared source file
 *  2. `instantiate()` the package against the verified source bundle
 *  3. belt-and-suspenders gates on the compiled output: `diffDeclaredOnly`
 *     (no structural mutation outside declared slots) +
 *     `verifyStructureHash` (masked structure still matches the package's
 *     authored hash)
 *  4. map the compiled bundle to a `LoadedApp` and create the project
 *  5. fire-and-forget the lineage persist (step 4's project already exists
 *     by this point — a lineage-persist failure must not roll it back or
 *     surface as an `instantiateTemplateToProject` failure; it's logged and
 *     swallowed)
 */
export async function instantiateTemplateToProject(
  entry: TemplateRegistryEntry,
  opts: { projectName: string; slotValues?: Record<string, unknown> },
): Promise<InstantiateToProjectResult> {
  const fetched = await fetchAndVerifySourceBundle(entry)
  if (!fetched.ok) return { ok: false, failures: fetched.failures }
  const sourceBundle = fetched.bundle

  const result = instantiate({
    pkg: entry.pkg,
    sourceBundle,
    slotValues: (opts.slotValues ?? {}) as Record<string, Json>,
  })
  if (!result.ok) return { ok: false, failures: result.failures }

  const diffFailures = diffDeclaredOnly(entry.pkg, sourceBundle, result.bundle)
  if (diffFailures.length > 0) return { ok: false, failures: diffFailures }

  const hashFailures = await verifyStructureHash(entry.pkg, result.bundle)
  if (hashFailures.length > 0) return { ok: false, failures: hashFailures }

  const loaded = buildLoadedTemplateFromFiles(entry.pkg.templateId, result.bundle.files)
  const projectId = await api.createProject(opts.projectName, loaded)

  // Fire-and-forget: lineage loss is non-fatal — the project was already
  // created successfully above, so a lineage-persist failure must not fail
  // this call or roll anything back. Logged so it isn't silently lost.
  api.persistProjectTemplateLineage(projectId, {
    templateId: entry.pkg.templateId,
    version: entry.pkg.version,
    structureHash: entry.pkg.source.structureHash,
  }).catch((err) => {
    console.warn('[instantiateTemplateToProject] failed to persist template lineage:', err)
  })

  return { ok: true, projectId }
}
